import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { selectableName } from '../../../styling/stylingHelper';
import { currencyFormatter } from '../../../services/helperService';
import { useInventoryStore } from '../../../stores/appInventoryStore';
import { formatNormalizedUnitPrice, InventorySectionProduct } from '../../../services/inventory/inventoryService';
import { CustomIngredient } from '../../../services/customIngredients/customIngredientsService';
import { TabbedSections } from '../../ui/TabbedSections';
import { LabeledFilterButton } from '../../ui/LabeledFilterButton';
import { InventorySlideOver, slideOverCancelClass, slideOverPrimaryClass } from '../../ui/InventorySlideOver';
import CustomIngredientDetailModal from '../costPrice/new/CostPrice/CustomIngredientDetailModal';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  restaurantId: string;
  sectionId: string;
  sectionName: string;
  /** Called with the freshly created assignments when the save succeeded without skips. */
  onAssigned?: (inserted: InventorySectionProduct[]) => void;
  /** Raise the drawer above another slide-over when it is opened as a second layer. */
  zIndexClass?: string;
  /**
   * Show the "Add to sections" picker on the review step, so the same products land in several
   * sections in one save. Only the config page (`/inventory/edit`) passes this — during a count
   * the drawer must stay single-section, since the caller consumes the counted section's rows.
   */
  allowMultipleSections?: boolean;
}

function SegmentedTab({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        // Three tabs share the row, so the label wraps to a second line on narrow screens
        // instead of overflowing; ≥sm the min-height + padding reproduce the old h-11 bar.
        "flex-1 min-w-0 min-h-11 px-2 sm:px-4 py-1.5 inline-flex items-center justify-center text-center",
        "text-[12px] sm:text-[14px] leading-4 sm:leading-5 font-semibold transition-colors",
        selected
          ? "bg-[#EDFCF5] dark:bg-[#28A878] text-[#0E8965] dark:text-[#E8F7F2] first:rounded-l-xl last:rounded-r-xl"
          : "bg-white text-[#344054] dark:bg-[#0E2B42] dark:text-[#CBD5E1] hover:bg-white first:rounded-l-xl last:rounded-r-xl"
      )}
    >
      {label}
    </button>
  );
}

function ProductCheckbox({ checked, disabled }: { checked: boolean; disabled?: boolean }) {
  return (
    <div
      className={cn(
        'w-5 h-5 rounded-[4px] border flex items-center justify-center flex-shrink-0 transition-colors',
        disabled && 'opacity-50',
        checked
          ? 'bg-[#0E8965] border-[#0E8965]'
          : 'border-[#D0D5DD] dark:bg-white dark:border-zinc-600 bg-white'
      )}
    >
      {checked && (
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3.33398 9.3335L5.66732 11.6668L12.6673 4.3335" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      )}
    </div>
  );
}

export default function AssignProductsModal({ isOpen, onClose, restaurantId, sectionId, sectionName, onAssigned, zIndexClass, allowMultipleSections }: Props) {
  const { t } = useTranslation();
  const {
    groups,
    sections,
    availableProducts,
    availableCustomIngredients,
    availableSubrecipes,
    isLoadingAvailable,
    loadAvailableProducts,
    loadAvailableCustomIngredients,
    loadAvailableSubrecipes,
    assignProducts,
  } = useInventoryStore();

  const [tab, setTab] = useState<'products' | 'custom' | 'subrecipe'>('products');
  const [search, setSearch] = useState('');
  const [overallType, setOverallType] = useState<string>('all');
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [step, setStep] = useState<'select' | 'review'>('select');
  // Name edits made on the review step, keyed by productKey — survive going Back.
  const [nameEdits, setNameEdits] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  // What the last save skipped. `sectionNames` is set only when a skip happened OUTSIDE this
  // drawer's own section (a fan-out skips per section), so the notice can name where.
  const [skipSummary, setSkipSummary] = useState<{ count: number; sectionNames: string[] | null } | null>(null);
  // Sections the save writes to. Always starts as (and keeps) the drawer's own section.
  const [targetSectionIds, setTargetSectionIds] = useState<string[]>([]);
  // Third layer: create a custom ingredient without leaving this flow.
  const [isCreateCustomOpen, setIsCreateCustomOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setTab('products');
    setSearch('');
    setOverallType('all');
    setSelectedPlatforms([]);
    setSelectedCategories([]);
    setSelected(new Set());
    setStep('select');
    setNameEdits({});
    setSaving(false);
    setSkipSummary(null);
    setTargetSectionIds([sectionId]);
    setIsCreateCustomOpen(false);
    loadAvailableProducts(restaurantId);
    loadAvailableCustomIngredients(restaurantId);
    loadAvailableSubrecipes(restaurantId);
  }, [isOpen, restaurantId, sectionId, loadAvailableProducts, loadAvailableCustomIngredients, loadAvailableSubrecipes]);

  // The create-ingredient modal releases the body scroll lock when it unmounts while this
  // drawer is still open — re-apply it (the slide-over's own effect owns the final release).
  useEffect(() => {
    if (!isOpen || isCreateCustomOpen) return;
    document.body.style.overflow = 'hidden';
  }, [isOpen, isCreateCustomOpen]);

  const overallTypeOptions = useMemo(() => ([
    { value: 'all', label: t('overallType.all') },
    { value: 'FOOD', label: t('overallType.food') },
    { value: 'DRINKS', label: t('overallType.drinks') },
    { value: 'NON_FOOD', label: t('overallType.nonFood') },
  ]), [t]);

  const typeScoped = useMemo(() => {
    if (overallType === 'all') return availableProducts;
    return availableProducts.filter(p => p.mainCategory === overallType);
  }, [availableProducts, overallType]);

  const platformOptions = useMemo(() => {
    return Array.from(new Set(typeScoped.map(p => p.platform).filter(Boolean))).sort();
  }, [typeScoped]);

  const categoryOptions = useMemo(() => {
    return Array.from(new Set(typeScoped.map(p => p.category).filter((c): c is string => !!c))).sort();
  }, [typeScoped]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return typeScoped.filter(p => {
      if (q && !p.productName.toLowerCase().includes(q)) return false;
      if (selectedPlatforms.length > 0 && !selectedPlatforms.includes(p.platform)) return false;
      if (selectedCategories.length > 0 && (!p.category || !selectedCategories.includes(p.category))) return false;
      return true;
    });
  }, [typeScoped, search, selectedPlatforms, selectedCategories]);

  const filteredCustom = useMemo(() => {
    const q = search.trim().toLowerCase();
    return availableCustomIngredients.filter(c => !q || c.productName.toLowerCase().includes(q));
  }, [availableCustomIngredients, search]);

  const filteredSubrecipes = useMemo(() => {
    const q = search.trim().toLowerCase();
    return availableSubrecipes.filter(s => !q || s.productName.toLowerCase().includes(q));
  }, [availableSubrecipes, search]);

  const toggle = (key: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Sections offered by the picker, in page order (group position, then section position).
  const sectionPicker = useMemo(() => {
    if (!allowMultipleSections) return [];
    const byGroup = [...groups]
      .sort((a, b) => a.position - b.position || a.createdAt.getTime() - b.createdAt.getTime())
      .map(g => ({
        groupId: g.id,
        groupName: g.name,
        list: sections
          .filter(s => s.groupId === g.id)
          .sort((a, b) => a.position - b.position || a.sectionName.localeCompare(b.sectionName)),
      }))
      .filter(g => g.list.length > 0);
    // A section whose group isn't in the store would otherwise be unreachable in the picker.
    const orphans = sections.filter(s => !groups.some(g => g.id === s.groupId));
    return orphans.length > 0 ? [...byGroup, { groupId: '', groupName: '', list: orphans }] : byGroup;
  }, [allowMultipleSections, groups, sections]);

  const showSectionPicker = allowMultipleSections === true && sections.length > 1;
  const multiSection = targetSectionIds.length > 1;

  // The drawer's own section is always a target: "Add products on Bar" that silently
  // skipped Bar would contradict its own title.
  const toggleTargetSection = (id: string) => {
    if (id === sectionId) return;
    setTargetSectionIds(prev => (prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]));
  };

  // One entry per selected product, in review order. A product already in this
  // section gets a SECOND row (variant) with a distinguishing default name —
  // editable on the review step and renameable afterwards via "Rename".
  //
  // Fanning out to more than one section drops that: the same "(2)" name would be minted in
  // every section, including the ones that never had the product. With several sections ticked
  // a product simply lands where it is missing and is skipped where it already is.
  const reviewRows = useMemo(() => {
    const orderRows = availableProducts
      .filter(p => selected.has(p.productKey))
      .map(p => {
        const again = !multiSection && p.assignedToSectionIds?.includes(sectionId) === true;
        return {
          productKey: p.productKey,
          defaultName: again ? `${p.productName} (2)` : p.productName,
          linkedName: p.productName,
          platform: p.platform || null,
          source: 'order' as const,
          customIngredientId: undefined as string | undefined,
          subrecipeId: undefined as string | undefined,
          asVariant: again || undefined,
        };
      });
    const customRows = availableCustomIngredients
      .filter(c => selected.has(c.productKey))
      .map(c => {
        const again = !multiSection && c.assignedToSectionIds?.includes(sectionId) === true;
        return {
          productKey: c.productKey,
          defaultName: again ? `${c.productName} (2)` : c.productName,
          linkedName: c.productName,
          platform: null,
          source: 'custom' as const,
          customIngredientId: c.customIngredientId as string | undefined,
          subrecipeId: undefined as string | undefined,
          asVariant: again || undefined,
        };
      });
    const subrecipeRows = availableSubrecipes
      .filter(s => selected.has(s.productKey))
      .map(s => {
        const again = !multiSection && s.assignedToSectionIds?.includes(sectionId) === true;
        return {
          productKey: s.productKey,
          defaultName: again ? `${s.productName} (2)` : s.productName,
          linkedName: s.productName,
          platform: null,
          source: 'subrecipe' as const,
          customIngredientId: undefined as string | undefined,
          subrecipeId: s.subrecipeId as string | undefined,
          asVariant: again || undefined,
        };
      });
    return [...orderRows, ...customRows, ...subrecipeRows];
  }, [availableProducts, availableCustomIngredients, availableSubrecipes, selected, sectionId, multiSection]);

  const handleSave = async () => {
    if (reviewRows.length === 0) return;
    // An emptied name falls back to the prefilled default — never save a blank row name.
    const products = reviewRows.map(r => ({
      productKey: r.productKey,
      productName: (nameEdits[r.productKey] ?? r.defaultName).trim() || r.defaultName,
      source: r.source,
      customIngredientId: r.customIngredientId,
      subrecipeId: r.subrecipeId,
      asVariant: r.asVariant,
    }));
    setSaving(true);
    try {
      const result = await assignProducts(targetSectionIds, products, restaurantId);
      if (result.skipped.length > 0) {
        // Partial skips are normal once several sections are ticked (the wine is already in the
        // bar): keep what was inserted, name the sections it was already in, and land back on
        // the picker so the next batch can be selected right away.
        const skippedElsewhere = result.skipped.some(s => s.existingSectionId !== sectionId);
        setSkipSummary({
          count: result.skipped.length,
          sectionNames: skippedElsewhere
            ? Array.from(new Set(result.skipped.map(s => s.existingSectionName).filter(Boolean)))
            : null,
        });
        setSelected(new Set());
        setNameEdits({});
        setStep('select');
        setSaving(false);
        return;
      }
      onAssigned?.(result.inserted);
      onClose();
    } catch (err) {
      console.error(err);
      setSaving(false);
    }
  };

  // A brand-new custom ingredient is immediately pickable here: refresh the list, land on
  // the custom tab and pre-select it so the user only has to hit Review.
  // (the modal closes itself right after this runs)
  const handleCustomIngredientCreated = async (ingredient: CustomIngredient) => {
    await loadAvailableCustomIngredients(restaurantId);
    setTab('custom');
    setSearch('');
    setSelected(prev => new Set(prev).add(`custom:${ingredient.id}`));
  };

  return (
    <InventorySlideOver
      isOpen={isOpen}
      onClose={onClose}
      title={`${t('inventory.addProducts')} — ${sectionName}`}
      description={t('inventory.tooltipProductKey')}
      closeLabel={t('inventory.cancel') as string}
      zIndexClass={zIndexClass}
      footer={
        step === 'select' ? (
          <>
            <button type="button" onClick={onClose} disabled={saving} className={slideOverCancelClass}>
              {t('inventory.cancel')}
            </button>
            <button
              type="button"
              onClick={() => setStep('review')}
              disabled={selected.size === 0}
              className={slideOverPrimaryClass}
            >
              {`${t('inventory.reviewSelection')} (${selected.size})`}
            </button>
          </>
        ) : (
          <>
            <button type="button" onClick={() => setStep('select')} disabled={saving} className={slideOverCancelClass}>
              {t('inventory.back')}
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || reviewRows.length === 0}
              className={slideOverPrimaryClass}
            >
              {saving
                ? t('inventory.loading')
                : multiSection
                  ? `${t('inventory.save')} (${reviewRows.length} → ${targetSectionIds.length} ${t('inventory.sectionsSuffix')})`
                  : `${t('inventory.save')} (${reviewRows.length})`}
            </button>
          </>
        )
      }
    >
      {skipSummary !== null && (
        <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
          <p className="text-sm text-yellow-800 dark:text-yellow-200">
            {skipSummary.sectionNames
              ? t('inventory.alreadyAssignedSummarySections', {
                  count: skipSummary.count,
                  sections: skipSummary.sectionNames.join(', '),
                })
              : t('inventory.alreadyAssignedSummary', { count: skipSummary.count })}
          </p>
        </div>
      )}

      {step === 'select' && (<>
      <div className="mb-5 sm:mb-6 flex rounded-xl border border-[#D0D5DD] dark:border-[#2D4F68]">
        <SegmentedTab
          label={t('inventory.tabOrderProducts')}
          selected={tab === 'products'}
          onClick={() => setTab('products')}
        />
        <div className="w-px bg-[#D0D5DD] dark:bg-[#2D4F68]" />
        <SegmentedTab
          label={t('inventory.tabCustomIngredients')}
          selected={tab === 'custom'}
          onClick={() => setTab('custom')}
        />
        <div className="w-px bg-[#D0D5DD] dark:bg-[#2D4F68]" />
        <SegmentedTab
          label={t('inventory.tabSubrecipes')}
          selected={tab === 'subrecipe'}
          onClick={() => setTab('subrecipe')}
        />
      </div>

      {tab === 'products' && (
        <div className="flex flex-cols gap-3 flex-wrap mb-5 min-[950px]:flex-row min-[950px]:items-center min-[950px]:justify-between">
          <TabbedSections
            options={overallTypeOptions}
            value={overallType}
            onChange={setOverallType}
            isTimeOptionSelection={false}
            maxTabsBeforeDropdown={4}
          />
          <div className="flex gap-2 relative">
            <LabeledFilterButton
              label={t('inventory.filterPlatform')}
              selected={selectedPlatforms}
              onChange={setSelectedPlatforms}
              options={platformOptions}
              valueType="platform"
            />
            <LabeledFilterButton
              label={t('inventory.filterCategory')}
              selected={selectedCategories}
              onChange={setSelectedCategories}
              options={categoryOptions}
              valueType="category"
            />
          </div>
        </div>
      )}

      <div className="relative w-full mb-5 sm:mb-6">
        <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-[#667085] dark:text-[#CBD5E1]" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M17.5 17.5L14.5834 14.5833M16.6667 9.58333C16.6667 13.4954 13.4954 16.6667 9.58333 16.6667C5.67132 16.6667 2.5 13.4954 2.5 9.58333C2.5 5.67132 5.67132 2.5 9.58333 2.5C13.4954 2.5 16.6667 5.67132 16.6667 9.58333Z" stroke="currentColor" strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('inventory.searchProducts') as string}
          className="w-full pl-10 pr-4 h-11 rounded-lg border border-[#D0D5DD] dark:border-[#2D4F68] bg-white dark:bg-[#14344C] text-[16px] leading-6 font-normal placeholder:text-[#667085] dark:placeholder:text-[#CBD5E1] text-zinc-900 dark:text-zinc-50 focus:outline-none"
        />
      </div>

      {/* Not in the list yet? Create it here instead of leaving for the cost-price page. */}
      {tab === 'custom' && (
        <button
          type="button"
          onClick={() => setIsCreateCustomOpen(true)}
          className="w-full mb-5 sm:mb-6 inline-flex items-center justify-center gap-2 h-11 px-4 rounded-xl border border-dashed border-[#0E8965] dark:border-[#28A878] text-[14px] leading-5 font-semibold text-[#0E8965] dark:text-[#28A878] hover:bg-[#EDFCF5] dark:hover:bg-[#0E89651A] transition-colors"
        >
          <Plus className="w-4 h-4" />
          {t('inventory.createCustomIngredientAction')}
        </button>
      )}

      <div className='border border-[#E8E8E9] dark:border-[#2D4F68] p-3 sm:p-4 rounded-lg'>
        <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar space-y-3 max-h-[calc(100vh-400px)] md:max-h-[calc(100vh-600px)]">
          {tab === 'subrecipe' ? (
            filteredSubrecipes.length === 0 ? (
              <p className="text-[14px] text-[#667085] dark:text-zinc-400 text-center py-8">
                {availableSubrecipes.length === 0 ? t('inventory.noSubrecipes') : '—'}
              </p>
            ) : filteredSubrecipes.map(s => {
              const isAssignedHere = s.assignedToSectionIds?.includes(sectionId);
              const otherSectionNames = (s.assignedToSectionNames || []).filter(
                (_, i) => s.assignedToSectionIds[i] !== sectionId
              );
              const isSelected = selected.has(s.productKey);
              const norm = formatNormalizedUnitPrice(s);

              return (
                <label
                  key={s.productKey}
                  className={cn(
                    'flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition',
                    isSelected
                      ? 'bg-[#EDFCF5] dark:bg-green-900/20 border-[#0E8965] dark:border-green-700'
                      : 'border-[#EAECF0] dark:border-[#1E3A4E] bg-[#F9F9FA] dark:bg-[#0E2B42] hover:bg-gray-50'
                  )}
                >
                  <div className="flex-1 min-w-0">
                    <p className={cn('text-[14px] sm:text-[16px] leading-5 font-semibold text-[#101828] dark:text-zinc-50', selectableName)}>
                      {s.productName}
                    </p>
                    <p className="text-[14px] sm:text-[16px] leading-5 sm:leading-6 font-medium text-[#667085] dark:text-zinc-400 mt-3">
                      <span className="text-[#344054] dark:text-zinc-300 font-medium">{t('inventory.subrecipeBadge')}</span>
                      <span> • {t('inventory.subrecipeYield', { portions: s.portions, unit: s.portionsUnit })}</span>
                      {norm && (
                        <span> • {currencyFormatter.format(norm.value)} / {norm.unit}</span>
                      )}
                      {isAssignedHere && (
                        <span> • {t('inventory.alreadyInSection', { section: sectionName })} • {t('inventory.addAgainHint')}</span>
                      )}
                    </p>
                    {otherSectionNames.length > 0 && (
                      <p className="text-[14px] sm:text-[16px] leading-5 sm:leading-6 font-medium text-[#667085] dark:text-zinc-400">
                        {t('inventory.alsoInSections', { sections: otherSectionNames.join(', ') })}
                      </p>
                    )}
                  </div>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggle(s.productKey)}
                    className="sr-only"
                  />
                  <ProductCheckbox checked={isSelected} />
                </label>
              );
            })
          ) : tab === 'custom' ? (
            filteredCustom.length === 0 ? (
              <p className="text-[14px] text-[#667085] dark:text-zinc-400 text-center py-8">
                {availableCustomIngredients.length === 0 ? t('inventory.noCustomIngredients') : '—'}
              </p>
            ) : filteredCustom.map(c => {
              const isAssignedHere = c.assignedToSectionIds?.includes(sectionId);
              const otherSectionNames = (c.assignedToSectionNames || []).filter(
                (_, i) => c.assignedToSectionIds[i] !== sectionId
              );
              const isSelected = selected.has(c.productKey);
              const norm = formatNormalizedUnitPrice(c);

              return (
                <label
                  key={c.productKey}
                  className={cn(
                    'flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition',
                    isSelected
                      ? 'bg-[#EDFCF5] dark:bg-green-900/20 border-[#0E8965] dark:border-green-700'
                      : 'border-[#EAECF0] dark:border-[#1E3A4E] bg-[#F9F9FA] dark:bg-[#0E2B42] hover:bg-gray-50'
                  )}
                >
                  <div className="flex-1 min-w-0">
                    <p className={cn('text-[14px] sm:text-[16px] leading-5 font-semibold text-[#101828] dark:text-zinc-50', selectableName)}>
                      {c.productName}
                    </p>
                    <p className="text-[14px] sm:text-[16px] leading-5 sm:leading-6 font-medium text-[#667085] dark:text-zinc-400 mt-3">
                      <span className="text-[#344054] dark:text-zinc-300 font-medium">{t('inventory.customBadge')}</span>
                      {norm && (
                        <span> • {currencyFormatter.format(norm.value)} / {norm.unit}</span>
                      )}
                      {isAssignedHere && (
                        <span> • {t('inventory.alreadyInSection', { section: sectionName })} • {t('inventory.addAgainHint')}</span>
                      )}
                    </p>
                    {otherSectionNames.length > 0 && (
                      <p className="text-[14px] sm:text-[16px] leading-5 sm:leading-6 font-medium text-[#667085] dark:text-zinc-400">
                        {t('inventory.alsoInSections', { sections: otherSectionNames.join(', ') })}
                      </p>
                    )}
                  </div>
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggle(c.productKey)}
                    className="sr-only"
                  />
                  <ProductCheckbox checked={isSelected} />
                </label>
              );
            })
          ) : isLoadingAvailable ? (
            <p className="text-[14px] text-[#667085] dark:text-zinc-400 text-center py-8">{t('inventory.loading')}</p>
          ) : filtered.length === 0 ? (
            <p className="text-[14px] text-[#667085] dark:text-zinc-400 text-center py-8">
              {availableProducts.length === 0 ? t('inventory.noAvailableProducts') : '—'}
            </p>
          ) : filtered.map(p => {
            const isAssignedHere = p.assignedToSectionIds?.includes(sectionId);
            const otherSectionNames = (p.assignedToSectionNames || []).filter(
              (_, i) => p.assignedToSectionIds[i] !== sectionId
            );
            const isSelected = selected.has(p.productKey);
            const norm = formatNormalizedUnitPrice(p);

            const metaParts: string[] = [];
            if (p.weightString) metaParts.push(p.weightString);
            if (p.platform) metaParts.push(p.platform);
            if (norm) metaParts.push(`${currencyFormatter.format(norm.value)} / ${norm.unit}`);
            if (typeof p.last60Qty === 'number') {
              metaParts.push(t('inventory.import.qty60', { value: Math.round(p.last60Qty * 10) / 10, unit: p.last60Unit ?? 'pcs' }));
            }

            return (
              <label
                key={p.productKey}
                className={cn(
                  'flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition',
                  isSelected
                    ? 'bg-[#0E89651A] dark:bg-green-900/20 border-[#0E8965] dark:border-green-700'
                    : 'border-[#EAECF0] dark:border-[#1E3A4E] bg-[#F9F9FA] dark:bg-[#0E2B42] hover:bg-gray-50'
                )}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] sm:text-[16px] leading-5 font-semibold text-[#101828] dark:text-zinc-50 truncate">
                    {p.productName}
                  </p>
                  <p className="text-[14px] sm:text-[16px] leading-5 sm:leading-6 font-medium text-[#667085] dark:text-zinc-400 mt-3">
                    {metaParts.join(' • ')}
                    {isAssignedHere && (
                      <span> • {t('inventory.alreadyInSection', { section: sectionName })} • {t('inventory.addAgainHint')}</span>
                    )}
                  </p>
                  {otherSectionNames.length > 0 && (
                    <p className="text-[14px] sm:text-[16px] leading-5 sm:leading-6 font-medium text-[#667085] dark:text-zinc-400">
                      {t('inventory.alsoInSections', { sections: otherSectionNames.join(', ') })}
                    </p>
                  )}
                </div>
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggle(p.productKey)}
                  className="sr-only"
                />
                <ProductCheckbox checked={isSelected} />
              </label>
            );
          })}
        </div>
      </div>
      </>)}

      {step === 'review' && (
        <>
          <p className="text-[14px] sm:text-[16px] leading-5 text-[#667085] dark:text-[#CBD5E1] mb-5 sm:mb-6">
            {t('inventory.reviewNamesHint')}
          </p>
          {showSectionPicker && (
            <div className="mb-5 sm:mb-6 border border-[#E8E8E9] dark:border-[#2D4F68] p-3 sm:p-4 rounded-lg">
              <p className="text-[14px] sm:text-[16px] leading-5 font-semibold text-[#101828] dark:text-zinc-50">
                {t('inventory.addToSectionsTitle')}
              </p>
              <p className="text-[12px] sm:text-[14px] leading-4 sm:leading-5 font-medium text-[#667085] dark:text-zinc-400 mt-1 mb-3">
                {t('inventory.addToSectionsHint')}
              </p>
              {/* Bounded on its own so a restaurant with many sections never pushes the
                  name rows below the fold on mobile. */}
              <div className="max-h-40 sm:max-h-56 overflow-y-auto custom-scrollbar space-y-3">
                {sectionPicker.map(g => (
                  <div key={g.groupId || 'ungrouped'}>
                    {g.groupName && (
                      <p className="text-[12px] leading-4 font-semibold uppercase tracking-wide text-[#667085] dark:text-zinc-400 mb-1.5">
                        {g.groupName}
                      </p>
                    )}
                    <div className="space-y-1.5">
                      {g.list.map(s => {
                        const isOrigin = s.id === sectionId;
                        const isTicked = targetSectionIds.includes(s.id);
                        return (
                          <label
                            key={s.id}
                            className={cn(
                              'flex items-center gap-3 p-2.5 rounded-xl border transition',
                              isOrigin ? 'cursor-default' : 'cursor-pointer',
                              isTicked
                                ? 'bg-[#EDFCF5] dark:bg-green-900/20 border-[#0E8965] dark:border-green-700'
                                : 'border-[#EAECF0] dark:border-[#1E3A4E] bg-[#F9F9FA] dark:bg-[#0E2B42] hover:bg-gray-50'
                            )}
                          >
                            <input
                              type="checkbox"
                              checked={isTicked}
                              disabled={isOrigin}
                              onChange={() => toggleTargetSection(s.id)}
                              className="sr-only"
                            />
                            <ProductCheckbox checked={isTicked} disabled={isOrigin} />
                            <span className="flex-1 min-w-0 truncate text-[14px] sm:text-[16px] leading-5 font-semibold text-[#101828] dark:text-zinc-50">
                              {s.sectionName}
                            </span>
                            {isOrigin && (
                              <span className="text-[12px] leading-4 font-medium text-[#667085] dark:text-zinc-400 flex-shrink-0">
                                {t('inventory.thisSectionBadge')}
                              </span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              {multiSection && (
                <p className="text-[12px] sm:text-[14px] leading-4 sm:leading-5 font-medium text-[#667085] dark:text-zinc-400 mt-3">
                  {t('inventory.multiSectionUnitsHint')}
                </p>
              )}
            </div>
          )}
          <div className="border border-[#E8E8E9] dark:border-[#2D4F68] p-3 sm:p-4 rounded-lg">
            <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar space-y-3 max-h-[calc(100vh-400px)] md:max-h-[calc(100vh-600px)]">
              {reviewRows.map(r => (
                <div
                  key={r.productKey}
                  className="p-3 rounded-xl border border-[#EAECF0] dark:border-[#1E3A4E] bg-[#F9F9FA] dark:bg-[#0E2B42]"
                >
                  <label className="block text-[12px] leading-4 font-medium text-[#667085] dark:text-[#CBD5E1] mb-1">
                    {t('inventory.renameProductLabel')}
                  </label>
                  <input
                    type="text"
                    value={nameEdits[r.productKey] ?? r.defaultName}
                    onChange={(e) => setNameEdits(prev => ({ ...prev, [r.productKey]: e.target.value }))}
                    className="w-full px-4 h-11 rounded-xl border border-[#D0D5DD] dark:border-[#1E3A4E] text-[14px] leading-5 font-semibold bg-white dark:bg-[#081C2C] text-zinc-900 dark:text-[#CBD5E1] focus:outline-none"
                  />
                  <p className="text-[14px] leading-5 font-medium text-[#667085] dark:text-zinc-400 mt-2">
                    {t('inventory.linkedProduct')}: <span className="text-[#344054] dark:text-zinc-300">{r.linkedName}</span>
                    {r.platform ? ` • ${r.platform}` : ''}
                    {r.source === 'custom' ? ` • ${t('inventory.customBadge')}` : ''}
                    {r.source === 'subrecipe' ? ` • ${t('inventory.subrecipeBadge')}` : ''}
                    {r.asVariant ? ` • ${t('inventory.extraRowBadge')}` : ''}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Layer 3 — portals itself to <body>, so it needs a z-index above this drawer. */}
      {isCreateCustomOpen && (
        <CustomIngredientDetailModal
          ingredient={null}
          restaurantId={restaurantId}
          onClose={() => setIsCreateCustomOpen(false)}
          onSaved={handleCustomIngredientCreated}
          zIndexClass="z-[70]"
        />
      )}
    </InventorySlideOver>
  );
}
